import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Hotel as HotelIcon,
  Group as GroupIcon,
  MeetingRoom as MeetingRoomIcon,
  AttachMoney as AttachMoneyIcon,
  Flood as FloodIcon,
  Construction as ConstructionIcon,
  People as PeopleIcon,
  Notes as NotesIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { AppraisalBlurb, AppraisalBlurbsResponse } from '@horwath-htl/shared-types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appraisal-tabpanel-${index}`}
      aria-labelledby={`appraisal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const AppraisalEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appraisal, setAppraisal] = useState<any>(null);

  // Initialize tab value from URL parameter if present
  const initialTab = searchParams.get('tab');
  const [tabValue, setTabValue] = useState(initialTab ? parseInt(initialTab, 10) : 0);
  const [formData, setFormData] = useState({
    name: '',
    status: '',
  });
  const [blurbs, setBlurbs] = useState<AppraisalBlurb[]>([]);
  const [blurbsLoading, setBlurbsLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string>('');
  const [operationId, setOperationId] = useState<string | null>(null);
  const [llmModels, setLlmModels] = useState<Array<{ id: number; modelName: string; provider: string; family: string }>>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [modelsLoading, setModelsLoading] = useState(false);

  useEffect(() => {
    fetchAppraisal();
  }, [id]);

  useEffect(() => {
    // Fetch blurbs when LLM Blurbs tab is opened
    if (tabValue === 9 && id) {
      fetchBlurbs();
      fetchLlmModels();
    }
  }, [tabValue, id]);

  useEffect(() => {
    // Poll operation status when generating
    if (operationId && generating) {
      const pollInterval = setInterval(() => {
        pollOperationStatus(operationId);
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(pollInterval);
    }
  }, [operationId, generating]);

  const fetchAppraisal = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authenticatedFetch(`${API_URL}/api/appraisals/${id}`);
      
      if (!response) {
        throw new Error('Failed to fetch appraisal: No response received');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to fetch appraisal: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        setAppraisal(data.data);
        // Handle array values by taking the first element
        const name = Array.isArray(data.data.name) ? data.data.name[0] : data.data.name;
        setFormData({
          name: name || '',
          status: 'active', // Default status since it's not in the response
        });
      } else {
        throw new Error(data.message || 'Failed to load appraisal');
      }
    } catch (err) {
      console.error('Error fetching appraisal:', err);
      setError(err instanceof Error ? err.message : 'Failed to load appraisal');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlurbs = async () => {
    if (!id) return;

    setBlurbsLoading(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/appraisals/${id}/property-blurbs`);

      if (!response) {
        throw new Error('Failed to fetch blurbs: No response received');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to fetch blurbs: ${response.statusText}`);
      }

      const data: AppraisalBlurbsResponse = await response.json();

      if (data.success && data.data) {
        setBlurbs(data.data);
      } else {
        throw new Error('Failed to load blurbs');
      }
    } catch (err) {
      console.error('Error fetching blurbs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load blurbs');
    } finally {
      setBlurbsLoading(false);
    }
  };

  const fetchLlmModels = async () => {
    setModelsLoading(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/models`);

      if (!response) {
        throw new Error('Failed to fetch LLM models: No response received');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to fetch LLM models: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.data && data.data.models) {
        setLlmModels(data.data.models.map((model: any) => ({
          id: model.id,
          modelName: model.modelName,
          provider: model.provider,
          family: model.family
        })));

        // Set default selection to first model if available
        if (data.data.models.length > 0) {
          setSelectedModel(data.data.models[0].modelName);
        }
      } else {
        throw new Error('Failed to load LLM models');
      }
    } catch (err) {
      console.error('Error fetching LLM models:', err);
      // Don't set error state for models, just log it
    } finally {
      setModelsLoading(false);
    }
  };

  const handleGenerateBlurbs = async () => {
    if (!id) return;

    setGenerating(true);
    setGenerationProgress('Starting blurb generation...');
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/appraisals/${id}/property-blurbs/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model_name: selectedModel || undefined
          }),
        }
      );

      if (!response) {
        throw new Error('Failed to start blurb generation: No response received');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to start blurb generation: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.operationId) {
        setOperationId(data.operationId);
        setGenerationProgress('Blurb generation started...');
      } else {
        throw new Error('Failed to start blurb generation');
      }
    } catch (err) {
      console.error('Error starting blurb generation:', err);
      setError(err instanceof Error ? err.message : 'Failed to start blurb generation');
      setGenerating(false);
    }
  };

  const pollOperationStatus = async (opId: string) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/operations/${opId}`);

      if (!response) {
        throw new Error('Failed to fetch operation status: No response received');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to fetch operation status: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.operation) {
        const status = data.operation.status;
        const progress = data.operation.progress_text || data.operation.progress?.step || '';

        setGenerationProgress(progress);

        if (status === 'completed') {
          setGenerating(false);
          setOperationId(null);
          setGenerationProgress('');
          // Refresh the blurbs table
          await fetchBlurbs();
        } else if (status === 'error') {
          setGenerating(false);
          setOperationId(null);
          setError(data.operation.error_message || 'Blurb generation failed');
          setGenerationProgress('');
        }
      }
    } catch (err) {
      console.error('Error polling operation status:', err);
      setGenerating(false);
      setOperationId(null);
      setError(err instanceof Error ? err.message : 'Failed to check operation status');
      setGenerationProgress('');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    // TODO: Implement save functionality when API endpoint is ready
    setError('Save functionality is not yet implemented. The existing API endpoint only updates property details.');
  };

  const handleCancel = () => {
    navigate('/appraisals');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Helper function to get value from array or single value
  const getValue = (val: any, index: number = 0) => {
    if (val === null || val === undefined) return null;
    return Array.isArray(val) ? val[index] : val;
  };

  // Helper function to format currency
  const formatCurrency = (value: number | null) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Helper function to format number
  const formatNumber = (value: number | null) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !appraisal) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/appraisals')}>
              Back to Appraisals
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  const propertyData = appraisal?.property_all_columns || appraisal?.property || {};

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          sx={{ mb: 3 }}
        >
          <Link 
            underline="hover" 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            sx={{ cursor: 'pointer' }}
          >
            Home
          </Link>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/appraisals'); }}
            sx={{ cursor: 'pointer' }}
          >
            Appraisals
          </Link>
          <Typography color="text.primary">
            {getValue(appraisal?.name) || 'Edit Appraisal'}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {getValue(appraisal?.name) || 'Appraisal'}
          </Typography>
          {propertyData.hubspot_deal_id && (
            <Chip 
              label={`HubSpot Deal: ${propertyData.hubspot_deal_id}`} 
              color="primary" 
              variant="outlined"
            />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={2} sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<HotelIcon />} label="Property Info" />
            <Tab icon={<LocationOnIcon />} label="Location" />
            <Tab icon={<BusinessIcon />} label="Building Details" />
            <Tab icon={<GroupIcon />} label="Ownership" />
            <Tab icon={<MeetingRoomIcon />} label="Meeting Space" />
            <Tab icon={<AttachMoneyIcon />} label="Financial" />
            <Tab icon={<FloodIcon />} label="FEMA/Flood" />
            <Tab icon={<ConstructionIcon />} label="Construction" />
            <Tab icon={<PeopleIcon />} label="Demographics" />
            <Tab icon={<NotesIcon />} label="LLM Blurbs" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HotelIcon /> Property Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Property Name</Typography>
                <Typography variant="body1">{propertyData.property_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Property ID</Typography>
                <Typography variant="body1">{getValue(propertyData.property_id, 1) || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Internal Property ID</Typography>
                <Typography variant="body1">{propertyData.internal_property_id || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Property Type</Typography>
                <Typography variant="body1">{propertyData.property_type || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Hotel Class</Typography>
                <Typography variant="body1">{propertyData.hotel_class || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Star Rating</Typography>
                <Typography variant="body1">{propertyData.star_rating ? `${propertyData.star_rating} Star` : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Scale</Typography>
                <Typography variant="body1">{propertyData.scale || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Brand</Typography>
                <Typography variant="body1">{propertyData.brand || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Parent Company</Typography>
                <Typography variant="body1">{propertyData.parent_company || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Number of Rooms</Typography>
                <Typography variant="body1">{formatNumber(propertyData.rooms)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Hotel Operator</Typography>
                <Typography variant="body1">{propertyData.hotel_operator || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Operation Type</Typography>
                <Typography variant="body1">{propertyData.operation_type || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Operational Status</Typography>
                <Typography variant="body1">{propertyData.operational_status || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">All Inclusive</Typography>
                <Typography variant="body1">{propertyData.all_inclusive || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">All Suites</Typography>
                <Typography variant="body1">{propertyData.all_suites || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Amenities</Typography>
                <Typography variant="body1">{propertyData.amenities || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon /> Location Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Address</Typography>
                <Typography variant="body1">{propertyData.property_address || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">City</Typography>
                <Typography variant="body1">{propertyData.city || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">State</Typography>
                <Typography variant="body1">{propertyData.state || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">ZIP Code</Typography>
                <Typography variant="body1">{propertyData.zip || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">County</Typography>
                <Typography variant="body1">{propertyData.county_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Country</Typography>
                <Typography variant="body1">{propertyData.country || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Continent</Typography>
                <Typography variant="body1">{propertyData.continent || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Subcontinent</Typography>
                <Typography variant="body1">{propertyData.subcontinent || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Market Name</Typography>
                <Typography variant="body1">{propertyData.market_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Submarket Name</Typography>
                <Typography variant="body1">{propertyData.submarket_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Submarket Cluster</Typography>
                <Typography variant="body1">{propertyData.submarket_cluster || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Hotel Location Type</Typography>
                <Typography variant="body1">{propertyData.hotel_location_type || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Latitude</Typography>
                <Typography variant="body1">{propertyData.latitude || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Longitude</Typography>
                <Typography variant="body1">{propertyData.longitude || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Cross Street</Typography>
                <Typography variant="body1">{propertyData.cross_street || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon /> Building Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Year Built</Typography>
                <Typography variant="body1">{propertyData.year_built || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Year Renovated</Typography>
                <Typography variant="body1">{propertyData.year_renovated || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Hotel Open Date</Typography>
                <Typography variant="body1">{propertyData.hotel_open_date || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Number of Stories</Typography>
                <Typography variant="body1">{formatNumber(propertyData.number_of_stories)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">RBA (sq ft)</Typography>
                <Typography variant="body1">{formatNumber(propertyData.rba)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Land Area (acres)</Typography>
                <Typography variant="body1">{propertyData.land_area_ac || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Land Area (sq ft)</Typography>
                <Typography variant="body1">{formatNumber(propertyData.land_area_sf)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Parking Spaces</Typography>
                <Typography variant="body1">{formatNumber(propertyData.number_of_parking_spaces)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Parking Spaces per Room</Typography>
                <Typography variant="body1">{propertyData.parking_spaces_per_room || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Building Status</Typography>
                <Typography variant="body1">{propertyData.building_status || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Building Class</Typography>
                <Typography variant="body1">{propertyData.building_class || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Construction Status</Typography>
                <Typography variant="body1">{propertyData.constr_status || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Zoning</Typography>
                <Typography variant="body1">{propertyData.zoning || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Restaurant</Typography>
                <Typography variant="body1">{propertyData.restaurant || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Typical Floor Size</Typography>
                <Typography variant="body1">{formatNumber(propertyData.typical_floor_size)}</Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon /> Ownership Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold">Owner Information</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Owner Name</Typography>
                <Typography variant="body1">{propertyData.owner_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Owner Phone</Typography>
                <Typography variant="body1">{propertyData.owner_phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Owner Address</Typography>
                <Typography variant="body1">{propertyData.owner_address || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Owner City/State/ZIP</Typography>
                <Typography variant="body1">{propertyData.owner_city_state_zip || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>True Owner Information</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">True Owner Name</Typography>
                <Typography variant="body1">{propertyData.true_owner_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">True Owner Phone</Typography>
                <Typography variant="body1">{propertyData.true_owner_phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">True Owner Address</Typography>
                <Typography variant="body1">{propertyData.true_owner_address || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">True Owner City/State/ZIP</Typography>
                <Typography variant="body1">{propertyData.true_owner_city_state_zip || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Recorded Owner Information</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Recorded Owner Name</Typography>
                <Typography variant="body1">{propertyData.recorded_owner_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Recorded Owner Phone</Typography>
                <Typography variant="body1">{propertyData.recorded_owner_phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Recorded Owner Address</Typography>
                <Typography variant="body1">{propertyData.recorded_owner_address || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Recorded Owner City/State/ZIP</Typography>
                <Typography variant="body1">{propertyData.recorded_owner_city_state_zip || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MeetingRoomIcon /> Meeting & Event Space
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Meeting Rooms</Typography>
                <Typography variant="body1">{formatNumber(propertyData.mtg_rooms)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Total Meeting Space (sq ft)</Typography>
                <Typography variant="body1">{formatNumber(propertyData.total_mtg_space)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Max Contiguous Meeting Space (sq ft)</Typography>
                <Typography variant="body1">{formatNumber(propertyData.max_contig_mtg_space)}</Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoneyIcon /> Financial Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">For Sale Status</Typography>
                <Typography variant="body1">{propertyData.for_sale_status || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">For Sale Price</Typography>
                <Typography variant="body1">{formatCurrency(propertyData.for_sale_price)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Price per Room</Typography>
                <Typography variant="body1">{formatCurrency(propertyData.for_sale_price_per_room)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Tax Year</Typography>
                <Typography variant="body1">{propertyData.tax_year || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Total Taxes</Typography>
                <Typography variant="body1">{formatCurrency(propertyData.taxes_total)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Taxes per sq ft</Typography>
                <Typography variant="body1">{propertyData.taxes_per_sf ? `$${propertyData.taxes_per_sf}` : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Building Tax Expenses</Typography>
                <Typography variant="body1">{formatCurrency(propertyData.building_tax_expenses)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Building Operating Expenses</Typography>
                <Typography variant="body1">{formatCurrency(propertyData.building_operating_expenses)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Cap Rate</Typography>
                <Typography variant="body1">{propertyData.cap_rate ? `${propertyData.cap_rate}%` : 'N/A'}</Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={6}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FloodIcon /> FEMA & Flood Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">FEMA Map Date</Typography>
                <Typography variant="body1">{propertyData.fema_map_date || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">FEMA Map Identifier</Typography>
                <Typography variant="body1">{propertyData.fema_map_identifier || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">FIRM Panel Number</Typography>
                <Typography variant="body1">{propertyData.firm_panel_number || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">In SFHA</Typography>
                <Typography variant="body1">{propertyData.in_sfha || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Floodplain Area</Typography>
                <Typography variant="body1">{propertyData.floodplain_area || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Flood Risk Area</Typography>
                <Typography variant="body1">{propertyData.flood_risk_area || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">FEMA Flood Zone</Typography>
                <Typography variant="body1">{propertyData.fema_flood_zone || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={7}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ConstructionIcon /> Construction Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Construction Material</Typography>
                <Typography variant="body1">{propertyData.construction_material || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Architect Name</Typography>
                <Typography variant="body1">{propertyData.architect_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Parcel Number 1</Typography>
                <Typography variant="body1">{propertyData.parcel_number_1_min || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Parcel Number 2</Typography>
                <Typography variant="body1">{propertyData.parcel_number_2_max || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Drive Ins</Typography>
                <Typography variant="body1">{propertyData.drive_ins || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">Collateral</Typography>
                <Typography variant="body1">{propertyData.collateral || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={8}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon /> Demographics (1 Mile Radius)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold">Population Age (2024)</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Average Age</Typography>
                <Typography variant="body1">{propertyData.pop_age_2024_avg_1m || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Median Age</Typography>
                <Typography variant="body1">{propertyData.pop_age_2024_med_1m || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Average Female Age</Typography>
                <Typography variant="body1">{propertyData.pop_age_2024_avg_female_1m || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Average Male Age</Typography>
                <Typography variant="body1">{propertyData.pop_age_2024_avg_male_1m || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Household Age Distribution (2024)</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">15-24 Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.hh_age_2024_15_24_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">25-34 Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.hh_age_2024_25_34_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">35-44 Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.hh_age_2024_35_44_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">45-54 Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.hh_age_2024_45_54_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">55-64 Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.hh_age_2024_55_64_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">65-74 Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.hh_age_2024_65_74_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">75-84 Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.hh_age_2024_75_84_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">85+ Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.hh_age_2024_85_plus_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Median Household Age</Typography>
                <Typography variant="body1">{propertyData.hh_age_2024_median_1m || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Population Groups (2024)</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Under 19 Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.pop_age_2024_less_than_19_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">20-64 Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.pop_age_2024_20_64_1m)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">65+ Years</Typography>
                <Typography variant="body1">{formatNumber(propertyData.pop_age_2024_65_plus_1m)}</Typography>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={9}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotesIcon /> LLM Blurbs
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 250 }} disabled={generating || modelsLoading}>
                  <InputLabel>LLM Model</InputLabel>
                  <Select
                    value={selectedModel}
                    label="LLM Model"
                    onChange={(e) => setSelectedModel(e.target.value)}
                  >
                    {llmModels.map((model) => (
                      <MenuItem key={model.id} value={model.modelName}>
                        {model.provider} - {model.family} ({model.modelName})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleGenerateBlurbs}
                  disabled={generating || blurbsLoading || !selectedModel}
                >
                  Generate/Regenerate Blurbs
                </Button>
              </Box>
            </Box>

            {generating && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {generationProgress || 'Generating blurbs...'}
                  </Typography>
                </Box>
              </Alert>
            )}

            {blurbsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : blurbs.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No property blurb prompts configured yet. Please configure prompts in the Property Blurb Prompts admin page.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>Order</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '150px' }}>Prompt ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '200px' }}>Prompt Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Generated Blurb</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blurbs.map((blurb) => (
                      <TableRow
                        key={blurb.prompt_id}
                        hover
                        onClick={() => navigate(`/appraisals/${id}/blurbs/${blurb.prompt_id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{blurb.process_order}</TableCell>
                        <TableCell>{blurb.prompt_id}</TableCell>
                        <TableCell>{blurb.prompt_name}</TableCell>
                        <TableCell>
                          {blurb.blurb ? (
                            <Typography variant="body2">
                              {blurb.blurb.length > 45
                                ? `${blurb.blurb.substring(0, 45)}...`
                                : blurb.blurb}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              Not generated yet
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={saving}
          >
            Back to Appraisals
          </Button>
        </Box>
      </Box>
    </Container>
  );
};